import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Icon } from '../../../components/Icon.tsx';

/**
 * Animated replica of the real WapMeet meeting flow (Dhivehi/RTL), faithful to
 * the app: the president approves submitted agenda items one by one → the
 * agenda is finalized → sent to the president for signing → signed & sent. Steps
 * on a timer and loops. Thaana strings are copied from WapMeet's i18n.
 *
 * The chrome is WapMeet's real Material 3 shell: a tinted chrome ground, a top
 * app bar, a Drive-style navigation drawer on the inline-start (RIGHT, since the
 * app is RTL) with a tonal pill on the active destination and a
 * tertiary-container "new meeting" button, and a white content panel with a
 * large rounded top corner. Colours are set INLINE from WapMeet's own M3 tokens
 * so the replica never inherits Office's theme.
 */

/* WapMeet's Material 3 light scheme (styles.css :root), as literal hex. */
const M3 = {
  chrome: '#F2F9F8',
  surface: '#FCFEFD',
  onSurface: '#181D1B',
  onSurfaceVariant: '#3F4946',
  outlineVariant: '#C3CDC8',
  primary: '#006A60',
  onPrimary: '#FFFFFF',
  primaryContainer: '#9EF2E4',
  onPrimaryContainer: '#00201C',
  secondaryContainer: '#CCE8E1',
  onSecondaryContainer: '#06201B',
  tertiaryContainer: '#CAE6FF',
  onTertiaryContainer: '#001E31',
  surfaceContainerLow: '#F4F8F6',
  surfaceContainerHigh: '#E4EAE7',
  surfaceContainerHighest: '#DEE4E1',
  successContainer: '#B6F0CE',
  onSuccessContainer: '#062111',
  warningContainer: '#FFE08D',
  onWarningContainer: '#251A00',
} as const;

// WapMeet's real type: Faruma body, MV MAG Round headings.
const BODY: React.CSSProperties = { fontFamily: 'Faruma, "Noto Sans Thaana", sans-serif' };
const HEADING = '"MV MAG Round", "MV FK", Faruma, "Noto Sans Thaana", sans-serif';
const TAB: React.CSSProperties = { fontVariantNumeric: 'tabular-nums' };

// The real navigation drawer's destinations (Sidebar.tsx + i18n/dv.ts labels).
const NAV = [
  { label: 'ޑޭޝްބޯޑް', icon: 'dashboard', active: false },
  { label: 'ނޮޓިފިކޭޝަން', icon: 'notifications', active: false },
  { label: 'ބައްދަލުވުންތައް', icon: 'event', active: true },
  { label: 'ތަފާސްހިސާބު', icon: 'bar_chart', active: false },
  { label: 'ތަންފީޒު', icon: 'fact_check', active: false },
  { label: 'ޕްރޮފައިލް', icon: 'person', active: false },
] as const;

const AGENDA = [
  { title: 'ފާއިތުވި ބައްދަލުވުމުގެ ޔައުމިއްޔާ ފާސްކުރުން', by: 'ސެކްރެޓަރީ ޖެނެރަލް' },
  { title: 'ބަނދަރު މަރާމާތުކުރުމުގެ ބަޖެޓް ފާސްކުރުން', by: 'ކައުންސިލްގެ ރައީސް' },
  { title: 'ރަމަޟާން މަހުގެ ޙަރަކާތްތަކުގެ ޕްރޮގްރާމް', by: 'ކައުންސިލް މެންބަރު' },
  { title: 'ފެނާއި ނަރުދަމާ މަޝްރޫޢުގެ ރިޕޯޓް', by: 'ކައުންސިލް އިދާރާ' },
];

// 0 all pending · 1-4 approve each · 5 finalized · 6 signed & sent.
const STEP_MS = [1150, 800, 800, 800, 1350, 1450];
const LAST = 6;

export function WapMeetScreen(): JSX.Element {
  const [step, setStep] = useState(0);

  useEffect(() => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      setStep(LAST);
      return;
    }
    const ms = step >= LAST ? 2800 : STEP_MS[step] ?? 1200;
    const t = setTimeout(() => setStep((s) => (s >= LAST ? 0 : s + 1)), ms);
    return () => clearTimeout(t);
  }, [step]);

  const approved = Math.min(AGENDA.length, step);
  const finalized = step >= 5;
  const sent = step >= LAST;

  const status = sent
    ? { label: 'އެޖެންޑާ ފޮނުވިފައި', bg: M3.successContainer, fg: M3.onSuccessContainer }
    : finalized
      ? { label: 'އެޖެންޑާ ފާސްކުރެވިފައި', bg: M3.primaryContainer, fg: M3.onPrimaryContainer }
      : { label: 'ތާވަލުކުރެވިފައި', bg: M3.secondaryContainer, fg: M3.onSecondaryContainer };

  return (
    <Shell>
      <div className="flex h-full flex-col px-2.5 py-2.5">
        {/* meeting header — a chrome-tinted card on the white panel */}
        <div className="shrink-0 rounded-2xl px-3 py-2.5" style={{ background: M3.chrome }}>
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="truncate text-[13px] font-bold leading-tight" style={{ fontFamily: HEADING }}>
                ކައުންސިލްގެ 12 ވަނަ ޢާއްމު ބައްދަލުވުން
              </p>
              <div className="mt-0.5 flex items-center gap-1 text-[9px]" style={{ color: M3.onSurfaceVariant }}>
                <span dir="ltr" style={TAB}>2026/12</span>
                <span>· ކައުންސިލް</span>
              </div>
            </div>
            <Chip bg={status.bg} fg={status.fg}>{status.label}</Chip>
          </div>

          <div
            className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[9.5px]"
            style={{ color: M3.onSurfaceVariant }}
          >
            <span className="inline-flex items-center gap-1">
              <Icon name="calendar_today" size={12} /> 12 ރަމަޟާން 1447
            </span>
            <span className="inline-flex items-center gap-1">
              <Icon name="schedule" size={12} />
              <span dir="ltr" style={TAB}>20:30</span>
            </span>
            <span className="inline-flex items-center gap-1">
              <Icon name="location_on" size={12} /> ކައުންސިލް ޖަލްސާކުރާ މާލަން
            </span>
          </div>
        </div>

        {/* agenda */}
        <div className="mt-2.5 min-h-0 flex-1">
          <div className="mb-1.5 flex items-center gap-2">
            <span className="text-[10px] font-bold" style={{ fontFamily: HEADING }}>އެޖެންޑާ</span>
            <span
              className="inline-flex items-center px-1.5 py-0.5 text-[8.5px] font-medium"
              style={{ background: M3.secondaryContainer, color: M3.onSecondaryContainer, borderRadius: '0.5rem' }}
            >
              <span dir="ltr" style={TAB}>ފާސްކޮށްފައި {approved}/{AGENDA.length}</span>
            </span>
            <span className="h-px flex-1" style={{ background: M3.outlineVariant }} />
          </div>

          <div className="overflow-hidden rounded-2xl" style={{ background: M3.surfaceContainerLow }}>
            {AGENDA.map((item, i) => {
              const ok = i < approved;
              return (
                <div
                  key={item.title}
                  className="flex items-start gap-2 px-2.5 py-1.5"
                  style={i > 0 ? { borderTop: '1px solid rgb(0 0 0 / 0.05)' } : undefined}
                >
                  <span
                    className="mt-0.5 grid size-6 shrink-0 place-items-center rounded-full text-[9px] font-semibold"
                    style={
                      ok
                        ? { background: M3.secondaryContainer, color: M3.onSecondaryContainer, ...TAB }
                        : { background: M3.surfaceContainerHighest, color: M3.onSurfaceVariant }
                    }
                  >
                    <span dir="ltr">{ok ? i + 1 : '·'}</span>
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[11px] font-medium leading-snug">{item.title}</p>
                    <p className="mt-0.5 truncate text-[9px]" style={{ color: M3.onSurfaceVariant }}>
                      {item.by}
                    </p>
                  </div>
                  {ok ? (
                    <span key="ok" className="ws-pop mt-0.5 shrink-0">
                      <Chip bg={M3.successContainer} fg={M3.onSuccessContainer}>ފާސް</Chip>
                    </span>
                  ) : (
                    <span className="mt-0.5 shrink-0">
                      <Chip bg={M3.warningContainer} fg={M3.onWarningContainer}>ފާސްކުރަން</Chip>
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* action area */}
        <div className="mt-2.5 shrink-0">
          {!finalized ? (
            approved < AGENDA.length ? (
              <div className="flex items-center justify-between gap-2 text-[9.5px]" style={{ color: M3.onSurfaceVariant }}>
                <span>ރައީސް އެޖެންޑާ އައިޓަމްތައް ފާސްކުރައްވަނީ…</span>
                <span
                  className="inline-flex shrink-0 items-center gap-1 px-1.5 py-0.5 text-[9px] font-medium"
                  style={{ background: M3.warningContainer, color: M3.onWarningContainer, borderRadius: '0.5rem' }}
                >
                  ފާސްކުރަން ހުރި <span dir="ltr" style={TAB}>{AGENDA.length - approved}</span>
                </span>
              </div>
            ) : (
              <span
                key="finalize"
                className="ws-pop flex items-center justify-center gap-1.5 rounded-full py-2 text-[11px] font-medium"
                style={{ background: M3.primary, color: M3.onPrimary }}
              >
                އެޖެންޑާ ފައިނަލްކުރޭ <Icon name="arrow_back" size={14} />
              </span>
            )
          ) : (
            <div
              key="doc"
              className="ws-fade flex items-center justify-between gap-2 rounded-2xl px-2.5 py-2"
              style={{ background: M3.surfaceContainerLow }}
            >
              <div className="flex min-w-0 items-center gap-2">
                <span
                  className="grid size-7 shrink-0 place-items-center rounded-full"
                  style={
                    sent
                      ? { background: M3.successContainer, color: M3.onSuccessContainer }
                      : { background: M3.primaryContainer, color: M3.onPrimaryContainer }
                  }
                >
                  <Icon name="fact_check" size={15} />
                </span>
                <div className="min-w-0">
                  <p className="truncate text-[10px] font-medium">އެޖެންޑާ ޑޮކިއުމަންޓް</p>
                  <p className="truncate text-[8.5px]" style={{ color: M3.onSurfaceVariant }}>
                    {sent ? (
                      <span className="inline-flex items-center gap-1" style={{ color: M3.primary }}>
                        <Icon name="check" size={10} /> ސޮއިކޮށްފައި
                      </span>
                    ) : 'ފައިނަލް · ސޮއި ބޭނުންވޭ'}
                  </p>
                </div>
              </div>
              {sent ? (
                <span
                  className="inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-[9px] font-medium"
                  style={{ background: M3.secondaryContainer, color: M3.onSecondaryContainer }}
                >
                  <Icon name="download" size={12} /> ޑައުންލޯޑް
                </span>
              ) : (
                <span
                  className="inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-[9px] font-medium"
                  style={{ background: M3.primary, color: M3.onPrimary }}
                >
                  <Icon name="draw" size={12} /> ސޮއިކުރުމަށް ފޮނުވާ
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </Shell>
  );
}

/* ── The M3 app shell — top app bar + navigation drawer + content panel ── */
function Shell({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div
      dir="rtl"
      className="app-screen h-full w-full overflow-hidden"
      style={{ ...BODY, background: M3.chrome, color: M3.onSurface }}
    >
      <div className="flex h-full flex-col">
        <TopBar />
        <div className="flex min-h-0 flex-1">
          <Drawer />
          <div className="min-w-0 flex-1 overflow-hidden rounded-t-2xl" style={{ background: M3.surface }}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

function Mark(): JSX.Element {
  // WapMeet's app mark (public/icons/wapmeet-mark.svg).
  return (
    <svg viewBox="0 0 512 512" aria-hidden className="size-6 shrink-0">
      <rect width="512" height="512" rx="128" fill="#0EA5A4" />
      <rect x="183" y="112" width="26" height="76" rx="13" fill="#ffffff" />
      <rect x="303" y="112" width="26" height="76" rx="13" fill="#ffffff" />
      <rect x="120" y="152" width="272" height="248" rx="44" fill="#ffffff" />
      <line x1="160" y1="212" x2="352" y2="212" stroke="#0EA5A4" strokeWidth={14} strokeLinecap="round" />
      <path
        d="M196 300 l44 44 l84 -104"
        fill="none"
        stroke="#0EA5A4"
        strokeWidth={32}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function TopBar(): JSX.Element {
  return (
    <div className="flex h-10 shrink-0 items-center gap-2 pl-3 pr-2.5">
      <Mark />
      <div className="min-w-0">
        <div className="text-[13px] font-bold leading-tight" style={{ fontFamily: HEADING }}>
          ވެޕްމީޓް
        </div>
        <div className="truncate text-[8.5px] leading-tight" style={{ color: M3.onSurfaceVariant }}>
          ކައުންސިލް
        </div>
      </div>
      <div className="flex-1" />
      <span className="hidden sm:block" style={{ color: M3.onSurfaceVariant }}>
        <Icon name="light_mode" size={15} />
      </span>
      <span style={{ color: M3.onSurfaceVariant }}>
        <Icon name="logout" size={15} />
      </span>
      <span
        className="mr-0.5 grid size-6 shrink-0 place-items-center rounded-full text-[9px] font-medium"
        style={{ background: M3.tertiaryContainer, color: M3.onTertiaryContainer }}
      >
        AN
      </span>
    </div>
  );
}

function Drawer(): JSX.Element {
  return (
    <div className="hidden w-[7.5rem] shrink-0 flex-col gap-0.5 px-1.5 pb-3 sm:flex">
      <span
        className="mb-2.5 inline-flex w-fit items-center gap-1.5 whitespace-nowrap rounded-2xl py-2 pl-3 pr-2 text-[9.5px] font-medium"
        style={{
          background: M3.tertiaryContainer,
          color: M3.onTertiaryContainer,
          boxShadow: '0 1px 2px rgb(0 0 0 / 0.12)',
        }}
      >
        <Icon name="add" size={15} /> އައު ބައްދަލުވުމެއް
      </span>

      {NAV.map((n) => (
        <span
          key={n.label}
          className="flex items-center gap-2 rounded-full px-2 py-1 text-[9.5px]"
          style={
            n.active
              ? { background: M3.secondaryContainer, color: M3.onSecondaryContainer, fontWeight: 500 }
              : { color: M3.onSurface }
          }
        >
          <span className="shrink-0" style={{ color: n.active ? 'inherit' : M3.onSurfaceVariant }}>
            <Icon name={n.icon} size={14} fill={n.active} />
          </span>
          <span className="truncate">{n.label}</span>
        </span>
      ))}
    </div>
  );
}

/* ── M3 chip — tonal, sentence case, 0.5rem radius, leading state dot ─── */
function Chip({ bg, fg, children }: { bg: string; fg: string; children: ReactNode }): JSX.Element {
  return (
    <span
      className="inline-flex shrink-0 items-center gap-1 px-1.5 py-0.5 text-[8.5px] font-medium"
      style={{ background: bg, color: fg, borderRadius: '0.5rem', letterSpacing: 0 }}
    >
      <span aria-hidden className="size-1 rounded-full" style={{ background: 'currentColor' }} />
      {children}
    </span>
  );
}
